import { IWatrRequest, IWatrReimbursement, IWageAndTravelVerifyValidation, IListItem } from '@nkr/models/index';
import { WageAndTravelViewType, WageAndTravelStatus, WageAndTravelType, WageAndTravelCategory, FileDocumentType, DashDonationType } from '@nkr/enums/index';
import { Button, Glyphicon } from 'react-bootstrap';
import { useEffect } from 'react';
import Drawer from 'rc-drawer';
import ViewDonorRequest from './view-donor-request';
import Verify from './verify/index';
import ReimburseWage from './reimburse-wages';
import ReimburseExpenses from './reimburse-expenses';
import { getDrawerTitle } from './helpers';
import {
    WatrDrawer,
    WatrDrawerBody,
    WatrDrawerFoot,
    WatrDrawerHead,
    WatrDrawerHeadActions,
    WatrDrawerPolicyLink,
} from '../styles';

import 'rc-drawer/assets/index.css';

interface IProps {
    data: IWatrRequest,
    reimbursements: IWatrReimbursement[],
    type: WageAndTravelViewType,
    show: boolean,
    validation?: IWageAndTravelVerifyValidation,
    isDirty?: boolean,
    category?: WageAndTravelCategory,
    openReconcileReimbursementEntry?: boolean,
    countryList?: IListItem[],
    onUpdateDonorRequest: (
        reqId: number, firstName: string, lastName: string, email: string, mobilePhone: string, dob: string, expirationDate: string,
        achAccountNumber: string, achRoutingNumber: string, intBankingNumber: string, intSwiftNumber: string,
        nameOfBank: string, cityTownLocality: string, stateProvinceRegion: string, postalCode: string, additionalCode: string,
        bankCountry: number | null) => void,
    onCancelView: () => void,
    getContainer: () => any,
    updateAudit: (requestId: number, type: WageAndTravelType, status: WageAndTravelStatus, reimbursementAmount: number, donationDate: string, incomePercentCovered: number, sixtyDayOverride: boolean, ninetyDayLWROverride: boolean, expenseLimitOverride: boolean) => void
}

type TProps = IProps;

const Sidebar = (props: TProps) => {
    const drawerTitle = getDrawerTitle(props.type);
    const centerBody =
        props.type === WageAndTravelViewType.ReimburseTravel
        || props.type === WageAndTravelViewType.ReimburseWage
        || props.type === WageAndTravelViewType.ReimburseWageAudit;

    useEffect(() => {
        if (!props.show) {
            return;
        }

        document.documentElement.classList.add('watr-request-open');

        return () => {
            document.documentElement.classList.remove('watr-request-open');
        };
    }, [props.show]);

    const getLink = () => {
        if (props.data?.donationOrganType == DashDonationType.Liver) {
            return 'https://www.donorshield.com/liverpolicy';
        }

        if (props.data?.country != 1) {
            return 'https://www.donorshield.com/intlpolicy/';
        }

        return 'https://www.donorshield.com/policy';
    }

    return (
        <Drawer
            width='min(720px, 100vw)'
            placement='right'
            getContainer={props.getContainer}
            open={props.show}
            autoFocus={false}
            style={{
                zIndex: 1300,
                pointerEvents: 'auto',
                overflowX: 'hidden',
                borderLeft: '7px solid transparent',
                height: '100%',
                padding: 0,
                boxSizing: 'border-box',
            }}
        >
            <WatrDrawer>
                <WatrDrawerHead>
                    <h3>{drawerTitle}</h3>
                    <WatrDrawerHeadActions>
                        <WatrDrawerPolicyLink href={getLink()} target="_blank">
                            Donor Shield Reimbursement Policy
                        </WatrDrawerPolicyLink>
                        <Button bsStyle="link" bsSize="sm" onClick={props.onCancelView}>
                            <Glyphicon glyph="remove" />
                        </Button>
                    </WatrDrawerHeadActions>
                </WatrDrawerHead>
                <WatrDrawerBody $center={centerBody}>
                    {props.data != null &&
                        <>
                            {props.type == WageAndTravelViewType.RequestData &&
                                <ViewDonorRequest
                                    data={props.data}
                                    isAudit={false}
                                    reimbursements={props.reimbursements}
                                    countryList={props.countryList}
                                    openReconcileReimbursementEntry={props.openReconcileReimbursementEntry}
                                    onUpdate={props.onUpdateDonorRequest}
                                />
                            }

                            {[WageAndTravelViewType.VerifyTravel, WageAndTravelViewType.VerifyWage, WageAndTravelViewType.VerifyStub, WageAndTravelViewType.VerifyAmount].indexOf(props.type) >= 0 &&
                                <Verify
                                    data={props.data}
                                    isAudit={props.type == WageAndTravelViewType.ReimburseWageAudit}
                                    reimbursements={props.reimbursements}
                                    category={props.category}
                                    validation={props.validation}
                                    isDirty={props.isDirty}
                                />
                            }

                            {(props.type === WageAndTravelViewType.ReimburseWage || props.type === WageAndTravelViewType.ReimburseWageAudit) && (
                                <ReimburseWage
                                    data={props.data}
                                    isAudit={props.type == WageAndTravelViewType.ReimburseWageAudit}
                                    reimbursements={props.reimbursements}
                                    onUpdateAudit={props.updateAudit}
                                />
                            )}

                            {props.type == WageAndTravelViewType.ReimburseTravel &&
                                <ReimburseExpenses
                                    data={props.data}
                                    isAudit={false}
                                    reimbursements={props.reimbursements}
                                />
                            }
                        </>
                    }
                </WatrDrawerBody>
                <WatrDrawerFoot className='text-center bg-info'>
                    <Button bsStyle='link' block onClick={props.onCancelView}>Close This View</Button>
                </WatrDrawerFoot>
            </WatrDrawer>
        </Drawer>
    );
}

export default Sidebar;